import { useMutation } from "@tanstack/react-query";
import { FieldArray, Form, Formik } from "formik";
import { PlusIcon } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import VStack from "../../components/layout/VStack/VStack";
import Text from "../../components/typography/Text/Text";
import FooterButton from "../../components/ui/Button/FooterButton";
import OutlinedButton from "../../components/ui/OutlinedButton";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useUser } from "../../hooks/useUser";
import HeaderComponent from "../Header/HeaderComponent";
import HeaderDesktop from "../Header/HeaderDesktop";
import provinceJson from "../Json/allprovince.json";
import { EditProfileForm } from "./_components/EditProfileForm";
import style from "./style.module.css";
import { aunjaiScService } from "../../services/aunjai-sc.service";
import { EditBirthDateConfirmDialog } from "./_components/EditBirthDateConfirmDialog";

const provinceData = provinceJson;

const EditMyProfilePage = () => {
  const { t } = useTranslation("requirement");
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState([]);
  const { userData, isLoading } = useUser();
  const [
    isShowEditBirthDateConfirmDialog,
    setIsShowEditBirthDateConfirmDialog,
  ] = useState(false);

  const { mutate: mutateUpdateCustomer } = useMutation({
    mutationFn: (values) => aunjaiScService.updateCustomer(values),
    onSuccess: () => {
      navigate(-1);
    },
  });

  const validationSchema = useMemo(() => {
    const isForeigner = userData?.is_foreigner;
    return Yup.object().shape({
      details: Yup.array().of(
        Yup.object().shape({
          dataType: Yup.string().required(t("editProfile.requireDataType")),
          prefix: Yup.string().when("dataType", {
            is: "prefix",
            then: (schema) => schema.required(t("editProfile.require1")),
            otherwise: (schema) => schema.notRequired(),
          }),
          status: Yup.string().when("dataType", {
            is: "prefix",
            then: (schema) => schema.required(t("editProfile.require13")),
            otherwise: (schema) => schema.notRequired(),
          }),
          firstNameTh: Yup.string().when("dataType", {
            is: "name",
            then: (schema) =>
              isForeigner
                ? schema.notRequired()
                : schema.required(t("editProfile.require2")),
            otherwise: (schema) => schema.notRequired(),
          }),
          lastNameTh: Yup.string().when("dataType", {
            is: "lastName",
            then: (schema) =>
              isForeigner
                ? schema.notRequired()
                : schema.required(t("editProfile.require3")),
            otherwise: (schema) => schema.notRequired(),
          }),
          firstNameEn: Yup.string().when("dataType", {
            is: "name",
            then: (schema) =>
              isForeigner
                ? schema.required(t("editProfile.require4"))
                : schema.notRequired(),
            otherwise: (schema) => schema.notRequired(),
          }),
          middleNameTh: Yup.string().optional(),
          middleNameEn: Yup.string().optional(),
          lastNameEn: Yup.string().when("dataType", {
            is: "lastName",
            then: (schema) =>
              isForeigner
                ? schema.required(t("editProfile.require5"))
                : schema.notRequired(),
            otherwise: (schema) => schema.notRequired(),
          }),
          birthDate: Yup.date().when("dataType", {
            is: "contact",
            then: (schema) => schema.required(t("editProfile.require6")),
            otherwise: (schema) => schema.notRequired(),
          }),
          phone: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) =>
              schema
                .matches(/^[0-9]{10}$/, t("editProfile.require9"))
                .required(t("editProfile.require10")),
            otherwise: (schema) => schema.notRequired(),
          }),
          email: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) =>
              schema
                .email(t("editProfile.require11"))
                .required(t("editProfile.require12")),
            otherwise: (schema) => schema.notRequired(),
          }),
          currentAddress: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) => schema.required(t("editProfile.require14")),
            otherwise: (schema) => schema.notRequired(),
          }),
          subDistrict: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) => schema.required(t("editProfile.require15")),
            otherwise: (schema) => schema.notRequired(),
          }),
          district: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) => schema.required(t("editProfile.require16")),
            otherwise: (schema) => schema.notRequired(),
          }),
          province: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) => schema.required(t("editProfile.require17")),
            otherwise: (schema) => schema.notRequired(),
          }),
          zipCode: Yup.string().when("dataType", {
            is: "contact",
            then: (schema) =>
              schema
                .matches(/^[0-9]{5}$/, t("editProfile.require18"))
                .required(t("editProfile.require18")),
            otherwise: (schema) => schema.notRequired(),
          }),
          file: Yup.mixed().when("dataType", {
            is: "contact",
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required("Please upload a file"),
          }),
        })
      ),
    });
  }, [t, userData.is_foreigner]);

  const initData = useMemo(() => {
    const province = provinceData.find((p) => p.name_th === userData?.province);

    const district = province?.amphure?.find(
      (a) => a.name_th === userData?.district
    );

    const subDistrict = district?.tambon?.find(
      (d) => d.name_th === userData?.sub_district
    );

    const newDistrictOptions = province?.amphure || [];
    const newSubDistrictOptions = district?.tambon || [];

    setDistrictOptions(newDistrictOptions);
    setSubDistrictOptions(newSubDistrictOptions);

    return {
      dataType: "",
      prefix: userData?.title || "",
      firstNameTh: userData?.first_name_th || "",
      lastNameTh: userData?.last_name_th || "",
      middleNameTh: userData?.middle_name_th || "",
      middleNameEn: userData?.middle_name_en || "",
      firstNameEn: userData?.first_name_en || "",
      lastNameEn: userData?.last_name_en || "",
      birthDate: userData?.date_of_birth || "",
      idCard: userData?.id_card || "",
      phone: userData?.phone || "",
      email: userData?.email || "",
      status: userData?.status || "",
      currentAddress: userData?.address || "",
      subDistrict: subDistrict?.id || "",
      district: district?.id?.toString() || "",
      province: province?.id?.toString() || "",
      zipCode: userData?.zip || "",
      file: null,
    };
  }, [userData]);

  const initialValues = {
    details: [initData],
  };

  const handleSubmit = (values) => {
    const formData = new FormData();
    const fileList = [];

    if (
      values.details.some(
        (detail) => detail.birthDate !== userData?.date_of_birth
      ) &&
      !isShowEditBirthDateConfirmDialog
    ) {
      setIsShowEditBirthDateConfirmDialog(true);
      return;
    }

    const reqData = values.details.map((detail) => {
      if (detail.dataType === "contact") {
        const province = provinceData.find(
          (p) => p.id === parseInt(detail.province)
        );
        const district = districtOptions.find(
          (d) => d.id === parseInt(detail.district)
        );
        const subDistrict = subDistrictOptions.find(
          (s) => s.id === parseInt(detail.subDistrict)
        );

        return {
          province: province?.name_th || "",
          district: district?.name_th || "",
          sub_district: subDistrict?.name_th || "",
          zip: detail.zipCode,
          address: detail.currentAddress,
          phone: detail.phone,
          email: detail.email,
          date_of_birth: detail.birthDate,
          id_card: userData?.id_card,
          request_type: "ข้อมูลทั่วไป",
        };
      }

      if (detail.dataType === "name") {
        fileList.push(detail.file?.file);
        return {
          first_name_th: detail.firstNameTh,
          first_name_en: detail.firstNameEn,
          middle_name_th: detail.middleNameTh,
          middle_name_en: detail.middleNameEn,
          id_card: userData?.id_card,
          request_type: "ชื่อ",
          file_name: detail.file?.file?.name,
        };
      }

      if (detail.dataType === "lastName") {
        fileList.push(detail.file?.file);
        return {
          last_name_th: detail.lastNameTh,
          last_name_en: detail.lastNameEn,
          id_card: userData?.id_card,
          request_type: "นามสกุล",
          file_name: detail.file?.file?.name,
        };
      }

      if (detail.dataType === "prefix") {
        fileList.push(detail.file?.file);
        return {
          title: detail.prefix,
          request_type: "สถานะภาพ",
          id_card: userData?.id_card,
          status: detail.status,
          file_name: detail.file?.file?.name,
        };
      }

      return {};
    });

    formData.append("data_list", JSON.stringify(reqData));
    fileList.forEach((file) => {
      formData.append("file_data", file);
    });
    mutateUpdateCustomer(formData);
  };

  if (isLoading) {
    return (
      <VStack>
        <HeaderComponent title={t("editProfile.title")} />
        <VStack padding={20} spacing={20}>
          <Text>{t("editProfile.loading")}</Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <div className={style.pageWrapper}>
      <VStack paddingBottom={100}>
        {isDesktop ? (
          <HeaderDesktop
            title={t("editProfile.title")}
            showIcon={true}
            onBack={true}
          />
        ) : (
          <HeaderComponent
            title={t("editProfile.title")}
            onBack={() => navigate(-1)}
          />
        )}
        <VStack>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, touched, errors }) => {
              return (
                <Form>
                  <FieldArray
                    name="details"
                    render={(arrayHelpers) => (
                      <VStack spacing={15}>
                        {values.details.map((data, index) => (
                          <EditProfileForm
                            key={index}
                            index={index}
                            userData={userData}
                            dataType={data.dataType}
                            touched={touched.details?.[index]}
                            errors={errors.details?.[index]}
                            remove={() => arrayHelpers.remove(index)}
                            canRemove={values.details.length > 1}
                            districtOptions={districtOptions}
                            subDistrictOptions={subDistrictOptions}
                            setDistrictOptions={setDistrictOptions}
                            setSubDistrictOptions={setSubDistrictOptions}
                          />
                        ))}
                        <OutlinedButton
                          onClick={() => arrayHelpers.push(initData)}
                          style={{
                            color: "#667085",
                            width: "fit-content",
                            alignSelf: "center",
                            padding: 20,
                            border: "1px solid #667085",
                          }}
                        >
                          <PlusIcon size={15} />
                          {t("add")}
                        </OutlinedButton>
                      </VStack>
                    )}
                  />
                  <FooterButton submitButtonTitle={t("editProfile.confirm")} />
                  <EditBirthDateConfirmDialog
                    isShow={isShowEditBirthDateConfirmDialog}
                    onClose={() => setIsShowEditBirthDateConfirmDialog(false)}
                  />
                </Form>
              );
            }}
          </Formik>
        </VStack>
      </VStack>
    </div>
  );
};

export default EditMyProfilePage;
